// services/api.js
import axios from "axios";

let API_BASE = "";

// 120s by default (ή πάρε από .env)
const AXIOS_TIMEOUT =
  Number(process.env.REACT_APP_AXIOS_TIMEOUT || 480000);

// Διαβάζει το backend_url.json δυναμικά, με fallback σε REACT_APP_BACKEND_URL
export async function getApiBase() {
  if (API_BASE) return API_BASE;

  // 1) Προσπάθεια από backend_url.json (absolute path για i18n subpaths)
  try {
    const url = `${window.location.origin}/backend_url.json`;
    const res = await fetch(url, { cache: "no-store" });
    if (res.ok) {
      const json = await res.json().catch(() => ({}));
      const backend_url = json?.backend_url;
      if (backend_url && typeof backend_url === "string") {
        API_BASE = backend_url.replace(/\/+$/, ""); // αφαίρεση τερματικών /
        return API_BASE;
      }
    }
    throw new Error("No backend_url found!");
  } catch (e) {
    console.warn("No backend_url.json available, trying REACT_APP_BACKEND_URL");
    // 2) Fallback σε .env
    const envBase = (process.env.REACT_APP_BACKEND_URL || "").trim();
    if (!envBase) {
      console.error("Missing REACT_APP_BACKEND_URL");
      throw e;
    }
    API_BASE = envBase.replace(/\/+$/, "");
    return API_BASE;
  }
}

function getNgrokHeader(apiBase) {
  // Καλύπτει και *.ngrok-free.app
  try {
    const u = new URL(apiBase);
    return u.hostname.includes("ngrok")
      ? { "ngrok-skip-browser-warning": "1" }
      : {};
  } catch {
    return apiBase.includes("ngrok")
      ? { "ngrok-skip-browser-warning": "1" }
      : {};
  }
}

// Βοηθητικό: ομογενοποίηση σφαλμάτων back/axios για UI
function normalizeError(err) {
  // Timeout από axios
  if (err?.code === "ECONNABORTED") {
    return "Timeout – ο διακομιστής άργησε να απαντήσει.";
  }

  // Απαντήσεις backend
  const resp = err?.response;
  if (resp) {
    let d = resp.data;

    // Αν (σπάνια) έρθει Blob/κείμενο
    if (d && typeof d === "string") {
      try { d = JSON.parse(d); } catch { /* ignore */ }
    }

    if (d && typeof d === "object") {
      const msg =
        d.reply ||
        d.error ||
        d.msg ||
        d.message ||
        null;
      if (resp.status === 401) return "UNAUTHORIZED";
      return msg || `HTTP ${resp.status}`;
    }

    if (resp.status === 401) return "UNAUTHORIZED";
    return `HTTP ${resp.status}`;
  }

  // Network / CORS / άλλα
  if (typeof err?.message === "string") {
    if (err.message.toLowerCase().includes("network error")) {
      return "Network error";
    }
    return err.message;
  }
  return "Network error";
}

// --- Δημιούργησε axios instance για κοινά headers/timeouts ---
async function makeAxiosConfig(extraHeaders = {}) {
  const apiBase = await getApiBase();
  return {
    baseURL: apiBase,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...getNgrokHeader(apiBase),
      ...extraHeaders,
    },
    timeout: AXIOS_TIMEOUT,
    withCredentials: false, // βάλε true μόνο αν το χρειάζεσαι
  };
}

// -------- Endpoints --------

// Login: επιστρέφει { token } ή { error }
export async function loginAdmin(username, password) {
  try {
    const cfg = await makeAxiosConfig();
    const res = await axios.post("/api/admin/login", { username, password }, cfg);
    return res.data; // { token }
  } catch (err) {
    return { error: normalizeError(err) || "Σφάλμα login!" };
  }
}

export async function fetchUsers(token) {
  try {
    const cfg = await makeAxiosConfig({ Authorization: `Bearer ${token}` });
    const res = await axios.get("/api/admin/users", cfg);
    return res.data;
  } catch (err) {
    const msg = normalizeError(err);
    if (msg === "UNAUTHORIZED") return { error: "UNAUTHORIZED" };
    console.log("users fetch error", err);
    return { error: msg || "Network error!" };
  }
}

export async function fetchUserMessages(user_id, token) {
  try {
    const cfg = await makeAxiosConfig({ Authorization: `Bearer ${token}` });
    const res = await axios.get(
      `/api/admin/messages?user_id=${encodeURIComponent(user_id)}`,
      cfg
    );
    return res.data;
  } catch (err) {
    return { error: normalizeError(err) || "Could not load messages." };
  }
}

// Σημαντικό: να ΜΗΝ κάνει throw – να επιστρέφει πάντα {reply|error, download_url?}
export async function sendAdminChat(payload, token) {
  try {
    const cfg = await makeAxiosConfig({ Authorization: `Bearer ${token || ""}` });
    const res = await axios.post("/api/admin/chat", payload, cfg);
    const data = res?.data || {};
    return {
      reply: data.reply ?? "",
      download_url: data.download_url ?? null,
    };
  } catch (err) {
    const msg = normalizeError(err);
    if (msg === "UNAUTHORIZED") return { error: "UNAUTHORIZED" };
    return { error: msg || "Σφάλμα αποστολής" };
  }
}

// Upload εικόνας (base64) προς backend
export async function uploadAdminImage({ fileBase64, filename, mime, token }) {
  try {
    const cfg = await makeAxiosConfig({ Authorization: `Bearer ${token || ""}` });
    const res = await axios.post(
      "/api/admin/upload-image",
      { base64: fileBase64, filename, mime },
      cfg
    );
    // backend returns: { url, filename, mime }
    return res.data;
  } catch (err) {
    const msg = normalizeError(err);
    if (msg === "UNAUTHORIZED") return { error: "UNAUTHORIZED" };
    return { error: msg || "Σφάλμα upload εικόνας" };
  }
}

export async function fetchAdminProjects(token) {
  try {
    const cfg = await makeAxiosConfig({
      Authorization: `Bearer ${token || ""}`,
    });
    const res = await axios.get("/api/admin/projects", cfg);
    return res.data;
  } catch (err) {
    return { error: normalizeError(err) || "Σφάλμα φόρτωσης έργων." };
  }
}
